(() => {
  const money = (cents, currency = "CAD") =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(
      Number(cents || 0) / 100,
    );

  const initializeCollection = (section) => {
    if (section.dataset.foxwoodReady === "true") return;
    section.dataset.foxwoodReady = "true";
    const items = [...section.querySelectorAll("[data-catalog-item]")];
    const filters = [...section.querySelectorAll("[data-catalog-filter]")];
    const search = section.querySelector("[data-catalog-search]");
    const empty = section.querySelector("[data-catalog-empty]");
    let active = "all";

    const apply = () => {
      const query = (search?.value || "").trim().toLowerCase();
      let visible = 0;
      items.forEach((item) => {
        const haystack = `${item.dataset.productTitle || ""} ${item.dataset.productType || ""} ${item.dataset.productTags || ""}`;
        const filterMatch = active === "all" || haystack.includes(active);
        const searchMatch = !query || haystack.includes(query);
        const show = filterMatch && searchMatch;
        item.hidden = !show;
        if (show) visible += 1;
      });
      if (empty) empty.hidden = visible !== 0;
    };

    filters.forEach((button) => {
      button.addEventListener("click", () => {
        active = button.dataset.catalogFilter || "all";
        filters.forEach((candidate) => {
          const isActive = candidate === button;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-pressed", String(isActive));
        });
        apply();
      });
    });
    search?.addEventListener("input", apply);
  };

  const initializeProduct = (section) => {
    if (section.dataset.foxwoodReady === "true") return;
    section.dataset.foxwoodReady = "true";
    const form = section.querySelector(".foxwood-product-form");
    if (!form) return;
    const variantSelect = form.querySelector("[data-variant-select]");
    const quantity = form.querySelector("[data-product-quantity]");
    const price = section.querySelector("[data-product-price]");
    const sku = section.querySelector("[data-product-sku]");
    const addButton = form.querySelector("[data-add-to-cart]");
    const minimumProperty = form.querySelector(
      "[data-product-minimum-property]",
    );
    const minimumLabel = form.querySelector("[data-quantity-minimum]");
    const incrementLabel = form.querySelector("[data-quantity-increment]");
    const maximumLabel = form.querySelector("[data-quantity-maximum]");
    const dataNode = section.querySelector("[data-variant-data]");
    const variants = dataNode ? JSON.parse(dataNode.textContent || "[]") : [];

    const personalization = form.querySelector(
      "[data-foxwood-personalization]",
    );
    const toggle = personalization?.querySelector(
      "[data-personalization-toggle]",
    );
    const field = personalization?.querySelector(
      "[data-personalization-field]",
    );
    const personalizationInput = personalization?.querySelector(
      "[data-personalization-input]",
    );
    const count = personalization?.querySelector(
      "[data-personalization-count]",
    );
    const preview = personalization?.querySelector(
      "[data-personalization-preview]",
    );
    const personalizationRequired =
      personalization?.dataset.required === "true";
    const customMinimum = Number(personalization?.dataset.minimum || 1);

    const activeVariant = () =>
      variants.find(
        (variant) => String(variant.id) === String(variantSelect?.value),
      );
    const syncVariant = () => {
      const variant = activeVariant();
      if (!variant) return;
      if (price)
        price.textContent =
          variant.priceFormatted || money(variant.price, variant.currency);
      if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : "";
      const personalizationEnabled = Boolean(
        personalization && (personalizationRequired || toggle?.checked),
      );
      const effectiveMinimum = Math.max(
        Number(variant.minimum || 1),
        personalizationEnabled ? customMinimum : 1,
      );
      if (quantity) {
        quantity.min = effectiveMinimum;
        quantity.step = variant.increment || 1;
        if (variant.maximum) quantity.max = variant.maximum;
        else quantity.removeAttribute("max");
        if (Number(quantity.value) < Number(quantity.min))
          quantity.value = quantity.min;
      }
      if (minimumProperty) minimumProperty.value = effectiveMinimum;
      if (minimumLabel) minimumLabel.textContent = String(effectiveMinimum);
      if (incrementLabel)
        incrementLabel.textContent = String(variant.increment || 1);
      if (maximumLabel)
        maximumLabel.textContent = variant.maximum
          ? String(variant.maximum)
          : "";
      if (addButton) {
        addButton.disabled =
          !variant.available ||
          (variant.maximum && effectiveMinimum > Number(variant.maximum));
        addButton.querySelector("span").textContent = variant.available
          ? "Add to wholesale order"
          : "Sold out";
      }
    };

    variantSelect?.addEventListener("change", syncVariant);
    form.querySelectorAll("[data-quantity-change]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!quantity) return;
        const direction = Number(button.dataset.quantityChange || 0);
        const step = Number(quantity.step || 1);
        const min = Number(quantity.min || 1);
        const max = quantity.max
          ? Number(quantity.max)
          : Number.POSITIVE_INFINITY;
        quantity.value = Math.min(
          max,
          Math.max(min, Number(quantity.value || min) + direction * step),
        );
        quantity.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    if (personalization) {
      const syncPersonalization = () => {
        const enabled = personalizationRequired || Boolean(toggle?.checked);
        if (field) field.hidden = !enabled;
        if (personalizationInput) {
          personalizationInput.disabled = !enabled;
          personalizationInput.required = enabled;
        }
        personalization.classList.toggle("is-active", enabled);
        syncVariant();
      };
      toggle?.addEventListener("change", syncPersonalization);
      personalizationInput?.addEventListener("input", () => {
        if (count)
          count.textContent = `${personalizationInput.value.length} / ${personalizationInput.maxLength}`;
        if (preview)
          preview.textContent = personalizationInput.value
            ? `“${personalizationInput.value}”`
            : "Your wording will appear here";
      });
      syncPersonalization();
    }
    syncVariant();
  };

  const initialize = (root = document) => {
    root
      .querySelectorAll("[data-foxwood-collection]")
      .forEach(initializeCollection);
    root.querySelectorAll("[data-foxwood-product]").forEach(initializeProduct);
  };

  document.addEventListener("DOMContentLoaded", () => initialize());
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
})();
